/**
 * useBusinessSettings — React Query hook for business settings.
 *
 * Exposes a single resource (GET) + update/revoke mutations. The Query
 * cache is scoped by the active tenant via `useTenant()` so switching
 * branches invalidates automatically.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../../shared/context/TenantContext';
import {
  fetchSettings,
  revokeAllOtherSessions,
  revokeSession,
  settingsQueryKeys,
  updateSettings,
} from './settings-api';
import type { BusinessSettingsPayload } from './types';

export function useBusinessSettings() {
  const { current } = useTenant();
  const scopeKey = current?.branchId ?? 'none';

  const query = useQuery({
    queryKey: [...settingsQueryKeys.root, scopeKey] as const,
    queryFn: fetchSettings,
    staleTime: 60_000,
  });

  return query;
}

export function useUpdateBusinessSettings() {
  const queryClient = useQueryClient();
  const { current } = useTenant();
  const scopeKey = current?.branchId ?? 'none';

  return useMutation({
    mutationFn: (patch: Partial<BusinessSettingsPayload>) => updateSettings(patch),
    onSuccess: (next) => {
      queryClient.setQueryData([...settingsQueryKeys.root, scopeKey], next);
    },
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.root });
    },
  });
}

export function useRevokeAllOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => revokeAllOtherSessions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.root });
    },
  });
}
